import { TEST_HOTEL_EMAIL } from '../data/workspaceTeam.js'
import { normalizeLoginEmail } from './productionLogin.js'

/** App account roles stored on Stream users as `doggosRole` (not Stream chat `role`). */
export const DOGGOS_ROLES = {
  Admin: 'Admin',
  PetOwner: 'PetOwner',
  PetBusiness: 'PetBusiness',
}

/** Roles the public signup API may assign. Never includes Admin. */
export const SIGNUP_ALLOWED_ROLES = Object.freeze([DOGGOS_ROLES.PetOwner])

/** Role assigned by the logged-out header Sign up flow. */
export const HEADER_SIGNUP_ROLE = DOGGOS_ROLES.PetOwner

/**
 * Emails that seed as Admin on login. Separate from the production login allowlist.
 * Keys must be normalizeLoginEmail() form.
 */
const ADMIN_EMAILS = new Set(['jacqueline@doggos.com'])

/**
 * Non-Admin emails that always seed as PetBusiness on login (listing managers).
 * Keys must be normalizeLoginEmail() form.
 */
const PET_BUSINESS_EMAILS = new Set([
  'manager@zpethotels.com',
  TEST_HOTEL_EMAIL,
])

export function isValidDoggosRole(role) {
  return Object.values(DOGGOS_ROLES).includes(role)
}

export function isSignupAllowedRole(role) {
  return SIGNUP_ALLOWED_ROLES.includes(role)
}

/**
 * Seed role from email only (login). Jacqueline → Admin; known hotel managers →
 * PetBusiness; everyone else → null so signup / existing Stream values decide.
 * Not derived from the production login allowlist.
 */
export function doggosRoleForEmail(email) {
  const normalized = normalizeLoginEmail(email)
  if (ADMIN_EMAILS.has(normalized)) {
    return DOGGOS_ROLES.Admin
  }
  if (PET_BUSINESS_EMAILS.has(normalized)) {
    return DOGGOS_ROLES.PetBusiness
  }
  return null
}

/**
 * True when the JWT/profile is an Admin. Accepts a role string, or an object
 * with `doggosRole` / `email`. Email fallback covers Jacqueline before Stream
 * is seeded with doggosRole.
 */
export function isDoggosAdmin(roleOrProfile) {
  if (!roleOrProfile) return false
  if (typeof roleOrProfile === 'string') {
    return roleOrProfile === DOGGOS_ROLES.Admin
  }
  if (roleOrProfile.doggosRole === DOGGOS_ROLES.Admin) return true
  return doggosRoleForEmail(roleOrProfile.email) === DOGGOS_ROLES.Admin
}

/** True when the profile is a PetBusiness (role claim or seeded manager email). */
export function isPetBusiness(roleOrProfile) {
  if (!roleOrProfile) return false
  if (typeof roleOrProfile === 'string') {
    return roleOrProfile === DOGGOS_ROLES.PetBusiness
  }
  // Prefer JWT / Stream claim so invited teammates (not on the email allowlist)
  // still get PetBusiness nav.
  if (roleOrProfile.doggosRole === DOGGOS_ROLES.PetBusiness) return true
  if (roleOrProfile.doggosRole === DOGGOS_ROLES.Admin) return false
  return doggosRoleForEmail(roleOrProfile.email) === DOGGOS_ROLES.PetBusiness
}

/** Owner CRM nav (e.g. Profile → pets) — PetOwner only. */
export function showsOwnerProfileNav(profile) {
  return Boolean(profile) && !isDoggosAdmin(profile) && !isPetBusiness(profile)
}

/** Business Dashboard nav — Admin and PetBusiness. */
export function showsDashboardNav(profile) {
  return isDoggosAdmin(profile) || isPetBusiness(profile)
}

/** Business workspace / team settings — Admin and PetBusiness. Pet owners only see Payments. */
export function showsWorkspaceSettingsNav(profile) {
  if (!profile) return false
  if (isDoggosAdmin(profile)) return true
  return isPetBusiness(profile)
}

/** Post-login / logo home for the signed-in user. */
export function defaultSignedInHomePath(profile) {
  return showsOwnerProfileNav(profile) ? '/pets/' : '/dashboard/'
}

/** Account settings entry for the signed-in user. */
export function defaultAccountSettingsPath(profile) {
  return showsWorkspaceSettingsNav(profile)
    ? '/account/'
    : '/account/personal/'
}
